import { Link, useNavigate } from 'react-router-dom';
import { Check, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MONTHLY_FEATURES = [
  'Full access to all 42 country profiles',
  'Real-time alerts and daily digest',
  'Unlimited reports',
  'Up to 10 users',
  'Email support',
  'Onboarding session included',
];

const ANNUAL_FEATURES = [
  'Everything in Monthly',
  'Priority support',
  'Quarterly business review',
  'Custom report templates',
  'Dedicated customer success manager',
  'SSO and advanced admin controls available on request',
];

export function PricingSection({ embedded = false }) {
  const navigate = useNavigate();

  const content = (
    <div className={embedded ? '' : 'min-h-screen bg-[#F5F5F5]'}>
      {!embedded && (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A2533]/95 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-white font-bold text-xl">Nuvanta Solutions</span>
              <span className="text-[#4DD0E1]">|</span>
              <span className="text-[#4DD0E1] font-semibold">TradePath</span>
            </Link>
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/signin')} variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-lg px-5">Sign in</Button>
              <Button onClick={() => navigate('/register')} className="bg-[#00707C] hover:bg-[#00707C]/90 text-white rounded-lg px-5">Start your trial</Button>
            </div>
          </div>
        </nav>
      )}
      <section className={`py-24 ${embedded ? '' : 'pt-32'} bg-white`} id="pricing" data-testid="pricing-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A2533] tracking-tight mb-3" style={{ fontFamily: "'Trebuchet MS', sans-serif" }}>
              Simple, transparent pricing
            </h2>
            <p className="text-base text-[#4E6974] max-w-2xl mx-auto" style={{ fontFamily: "'Calibri', sans-serif" }}>
              One platform, all 42 countries with global coverage, 12 verified data sources, 24/7 monitoring.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Monthly */}
            <div className="bg-white border border-[#E0E0E0] rounded-2xl p-8 flex flex-col" data-testid="pricing-card-monthly">
              <h3 className="text-xl font-bold text-[#0A2533] mb-2" style={{ fontFamily: "'Trebuchet MS', sans-serif" }}>Monthly</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-[#0A2533]">$2,500</span>
                <span className="text-[#4E6974] text-sm">per month + GST</span>
              </div>
              <p className="text-sm text-[#4E6974] mb-6">Billed monthly, cancel any time</p>
              <ul className="space-y-3 mb-8 flex-1">
                {MONTHLY_FEATURES.map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#0A2533]">
                    <Check className="h-4 w-4 text-[#0097A7] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button onClick={() => navigate('/register?plan=monthly')} className="w-full bg-[#0D3B4F] hover:bg-[#0A2533] text-white rounded-lg py-3" data-testid="pricing-monthly-btn">
                Start monthly subscription
              </Button>
            </div>

            {/* Annual */}
            <div className="relative bg-white border-2 border-[#0097A7] rounded-2xl p-8 flex flex-col shadow-lg" data-testid="pricing-card-annual">
              <div className="absolute -top-3 right-6 bg-[#00707C] text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                <Star className="h-3 w-3" /> Save ~17%
              </div>
              <h3 className="text-xl font-bold text-[#0A2533] mb-2" style={{ fontFamily: "'Trebuchet MS', sans-serif" }}>Annual</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-[#0A2533]">$25,000</span>
                <span className="text-[#4E6974] text-sm">per year + GST</span>
              </div>
              <p className="text-sm text-[#4E6974] mb-6">Billed annually, paid upfront. Save ~17% versus monthly.</p>
              <ul className="space-y-3 mb-8 flex-1">
                {ANNUAL_FEATURES.map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#0A2533]">
                    <Check className="h-4 w-4 text-[#0097A7] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button onClick={() => navigate('/register?plan=annual')} className="w-full bg-[#00707C] hover:bg-[#005F6A] text-white rounded-lg py-3" data-testid="pricing-annual-btn">
                Start annual subscription
              </Button>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-xs text-[#4E6974] max-w-2xl mx-auto">
              Prices in AUD, GST exclusive. Both plans include access for your whole organisation. Need more than 10 users, on-premises deployment, or enterprise procurement? <Link to="/contact" className="text-[#0097A7] hover:underline">Contact us for a custom quote</Link>.
            </p>
            <p className="text-xs text-[#4E6974] mt-3">
              Questions about pricing?{' '}
              <a href="mailto:info@nuvantasolutions.com.au" className="text-[#0097A7] hover:underline">Contact sales</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );

  return content;
}

export default function PricingPage() {
  return <PricingSection />;
}
